/**
 * @file: 运营 request
 */
import { ApiConfigs, Response } from '@schemas/service';
import {
  IOfficalAccountStatus,
  IBannerList,
  IHomeTabs,
  ISubjectTags,
  IMenu,
  IDoctors,
  IActivity,
  IAuthorWeekly,
  IPopupActivity,
  IHomePageConsultingOrder
} from '@schemas/operation';
import { ISubjects } from '@/schemas/video';
import { checkResponse, checkResponseCode } from '@/utils/helpers';

import { request } from './request';

export const api: ApiConfigs = {
  queryAllBanners: {
    url: '/health/api/v1/home/queryBannerList',
    method: 'GET'
  },
  queryOfficalAccountStatus: {
    url: '/health/api/v1/fans/count',
    method: 'GET'
  },
  queryHomeTabs: {
    url: '/health/api/v1/home/queryTabIndex',
    method: 'GET'
  },
  querySubjectTags: {
    url: '/health/api/v1/home/querySubjectTagCollection',
    method: 'GET'
  },
  queryHeadlines: {
    url: '/health/api/v1/home/queryHealthHeadlines',
    method: 'GET'
  },
  queryMenu: {
    url: 'health/api/v1/home/queryFeatureEntrance',
    method: 'GET'
  },
  queryServiceMenu: {
    url: 'health/api/v1/home/queryMainFeatureEntrance',
    method: 'GET'
  },
  queryActivity: {
    url: '/health/api/v1/homeCard/get',
    // url: 'https://kapi.corp.kuaishou.com/mock/5058/health/api/v1/homeCard/get',
    method: 'GET'
  },
  queryAuthorWeekly: {
    url: '/health/api/v1/fans/authorWeekly',
    method: 'GET'
  },
  queryRecommendDoctorAuthors: {
    url: '/health/api/v1/home/queryRecommendDoctorAuthors',
    method: 'GET'
  },
  // 根据定位和白名单查询是否可以进入挂号首页
  querySupportRegister: {
    url: '/health/api/v1/home/homeRegisterConditionCheck',
    method: 'GET'
  },
  postRegisterBook: {
    url: '/health/api/v1/home/homeRegisterInfoCollection',
    method: 'POST'
  },
  // 获取活动数据
  queryPopupActivities: {
    url: '/health/api/v1/activity/queryPopupActivities',
    method: 'GET'
  },
  // 首页咨询中订单卡片
  homePageConsultCard: {
    url: '/health/api/v1/home/homePageConsultCard',
    method: 'GET'
  }
};

const operation = {
  async getBannerList() {
    try {
      const response: Response<IBannerList> = await request(
        api.queryAllBanners
      );
      const { result } = response;
      if (result) {
        return result;
      }
      return [];
    } catch (error) {
      // TODO
      return Promise.reject(error);
    }
  },

  async getOfficialAccount() {
    try {
      const response: Response<IOfficalAccountStatus> = await request(
        api.queryOfficalAccountStatus
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getHomeTabs() {
    try {
      const response: Response<IHomeTabs> = await request(api.queryHomeTabs);
      const { result = [] } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getSubjectTags() {
    try {
      const response: Response<ISubjectTags> = await request(
        api.querySubjectTags
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getHeadlines() {
    try {
      const response: Response<ISubjects> = await request(api.queryHeadlines);
      const { result = [] } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getMenu() {
    const key = 'health_get_menu_cache';
    const update = async () => {
      try {
        const response: Response<IMenu[]> = await request(
          api.queryMenu,
          undefined,
          undefined,
          { hideGlobalLoading: false }
        );
        const { result } = response;
        if (result) {
          window.localStorage.setItem(key, JSON.stringify(result));
          return result;
        }
        return [];
      } catch (error) {
        return Promise.reject(error);
      }
    };
    const value = window.localStorage.getItem(key);
    if (value) {
      try {
        update();
        return JSON.parse(value);
      } catch (error) {
        window.localStorage.removeItem(key);
      }
    }
    return update();
  },

  async getServiceMenu() {
    try {
      // eslint-disable-next-line no-underscore-dangle
      const cacheData = window.__MAIN_FEATURE_ENTRANCE__;
      if (cacheData) {
        return cacheData;
      }
      const response: Response<IMenu[]> = await request(
        api.queryServiceMenu,
        undefined,
        undefined,
        { hideGlobalLoading: false }
      );
      const { result } = response;
      if (result) {
        return result;
      }
      return [];
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getActivity() {
    try {
      const response: Response<IActivity | null> = await request(
        api.queryActivity
      );
      const { result } = response;
      if (result) {
        return result;
      }
      return {} as IActivity;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getAuthorWeekly(uniqueId, doctorId) {
    try {
      const response: Response<IAuthorWeekly | null> = await request(
        api.queryAuthorWeekly,
        {
          doctorId,
          uniqueId
        }
      );
      const { result } = response;
      if (result) {
        return result;
      }
      return {} as IAuthorWeekly;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getRecommendDoctors() {
    try {
      const response: Response<IDoctors> = await request(
        api.queryRecommendDoctorAuthors,
        undefined,
        undefined,
        { hideGlobalLoading: false }
      );
      const { result } = response;
      if (result) {
        return result;
      }
      return [];
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // 对接健康之路挂号入口开放后使用, 需要根据经纬度查询当前用户是否有挂号权限
  async supportRegister(payload) {
    const { longitude, latitude } = payload;
    const params = {
      longitude,
      latitude
    };
    try {
      const response: Response<any> = await request(
        api.querySupportRegister,
        params
      );
      const { result } = response;
      if (result) {
        return true;
      }
      return false;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // 2021-07-14 挂号1.2上线不开放入口使用, 不需要经纬度信息, 挂号1.2入口正式开放后删除即可
  async supportRegisterOld() {
    try {
      const response: Response<any> = await request(api.querySupportRegister);
      const { result } = response;
      if (result) {
        return true;
      }
      return false;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async postRegisterBook({ city = '', latitude = 0, longitude = 0 }) {
    try {
      const response: Response<any> = await request(api.postRegisterBook, {
        city,
        latitude,
        longitude
      });
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async queryPopupActivities() {
    try {
      const response: Response<IPopupActivity[] | null> = await request(
        api.queryPopupActivities
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result as IPopupActivity[];
      }
      return Promise.reject(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async homePageConsultCard() {
    try {
      const response: Response<IHomePageConsultingOrder | null> = await request(
        api.homePageConsultCard,
        undefined,
        undefined,
        {
          hideToast: true,
          hideGlobalLoading: true
        }
      );
      const { code, result } = response;
      if (checkResponseCode(code)) {
        return result as IHomePageConsultingOrder | null;
      }
      return Promise.reject(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }
};

export default operation;
